import { Empty, Pagination, Spin, Typography } from 'antd'
import { useOrgSite } from 'features/fde'
import {
  INBOX_DARK_BACKGROUND,
  INBOX_DARKER_BACKGROUND,
} from 'features/inbox/colors'
import { InboxListHeader } from 'features/inbox/components/InboxListHeader'
import { InspectionCard } from 'features/inbox/components/inspectionCards/InspectionCard'
import { useInbox } from 'features/inbox/contexts/InboxContext'
import { useInspectionData } from 'features/inbox/hooks/useInspectionData'
import { EnrichedGeckoInspection } from 'features/inbox/types/types'
import React, { memo, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

import { getColorForAsset } from './utils'

const { Text } = Typography

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: ${INBOX_DARKER_BACKGROUND};
`

const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  height: 100%;
  align-items: flex-start;
  gap: 1rem;
`

const CardListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  padding-bottom: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: ${INBOX_DARKER_BACKGROUND};
`

const StyledPagination = styled(Pagination)`
  .ant-pagination-item {
    background-color: ${CantileverTheme.colors.grayscale['600']};
    border-color: ${CantileverTheme.colors.grayscale['500']};

    a {
      color: ${CantileverTheme.colors.grayscale['200']};
    }
  }

  .ant-pagination-item-active {
    background-color: ${CantileverTheme.colors.alternate['400']};
    border-color: ${CantileverTheme.colors.alternate['400']};

    a {
      color: ${CantileverTheme.colors.grayscale['100']};
    }
  }

  .ant-pagination-prev,
  .ant-pagination-next,
  .ant-pagination-jump-prev,
  .ant-pagination-jump-next {
    .ant-pagination-item-link {
      background-color: ${CantileverTheme.colors.grayscale['600']};
      border-color: ${CantileverTheme.colors.grayscale['500']};
      color: ${CantileverTheme.colors.grayscale['200']};
    }
  }

  .ant-pagination-options {
    .ant-select-selector {
      background-color: ${CantileverTheme.colors.grayscale['600']};
      border-color: ${CantileverTheme.colors.grayscale['500']};
      color: ${CantileverTheme.colors.grayscale['200']};
    }
  }

  .ant-pagination-total-text {
    color: ${CantileverTheme.colors.grayscale['300']};
  }
`

const PaginationContainer = styled.div`
  padding: 1rem;
  display: flex;
  justify-content: center;
  border-top: 1px solid ${CantileverTheme.colors.grayscale['600']};
  margin-top: 1rem;
  margin-bottom: 1rem;
  background-color: ${INBOX_DARK_BACKGROUND};
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 20rem;
  gap: 1rem;
`

const StyledSpin = styled(Spin)`
  .ant-spin-dot-item {
    background-color: ${CantileverTheme.colors.alternate['400']};
  }
`

const InspectionCardsListComponent: React.FC = () => {
  const { state, setSelectedInspection, setPagination } = useInbox()
  const { siteId, orgId } = useOrgSite()

  const { inspections, isLoading } = useInspectionData({
    siteId: siteId ?? undefined,
    orgId: orgId ?? undefined,
    sortDirection: state.sortDirection,
    filters: state.filters,
  })

  const unitIdsKey = useMemo(() => {
    const unitIds = [...new Set(inspections.map((i) => i.tags.unit))].sort()
    return unitIds.join(',')
  }, [inspections])

  const unitColors = useMemo<Record<string, string>>(() => {
    const unitIds = unitIdsKey.split(',').filter(Boolean)
    const colorsByUnit = unitIds.map((id) => [id, getColorForAsset(id)])
    return Object.fromEntries(colorsByUnit)
  }, [unitIdsKey])

  const startIndex =
    (state.pagination.currentPage - 1) * state.pagination.pageSize
  const endIndex = startIndex + state.pagination.pageSize
  const paginatedInspections = inspections.slice(startIndex, endIndex)
  const totalEntries = inspections.length

  const handleCardClick = useCallback(
    (inspectionId: string) => {
      setSelectedInspection(inspectionId)
    },
    [setSelectedInspection]
  )

  const handlePageChange = useCallback(
    (page: number, pageSize?: number) => {
      setPagination({
        currentPage: page,
        ...(pageSize && { pageSize }),
        total: totalEntries ?? 0,
      })
    },
    [setPagination, totalEntries]
  )

  if (isLoading) {
    return (
      <MainContainer style={{ height: '100%' }}>
        <InboxListHeader />
        <CardListContainer>
          <LoadingContainer>
            <StyledSpin size='large' />
            <Text style={{ color: CantileverTheme.colors.grayscale['400'] }}>
              Loading inspections...
            </Text>
          </LoadingContainer>
        </CardListContainer>
      </MainContainer>
    )
  }

  if (inspections.length === 0) {
    return (
      <MainContainer>
        <InboxListHeader />
        <CardListContainer>
          <Empty
            description={
              <Text style={{ color: CantileverTheme.colors.grayscale['400'] }}>
                No inspections found
              </Text>
            }
            style={{ marginTop: '2rem' }}
          />
        </CardListContainer>
      </MainContainer>
    )
  }

  return (
    <MainContainer>
      <InboxListHeader />
      <ContentContainer>
        <CardListContainer>
          {paginatedInspections.map((inspection: EnrichedGeckoInspection) => (
            <InspectionCard
              key={inspection.id}
              inspection={inspection}
              isSelected={state.selectedInspectionId === inspection.id}
              unitColors={unitColors}
              onCardClick={handleCardClick}
            />
          ))}
          <PaginationContainer>
            <StyledPagination
              current={state.pagination.currentPage}
              pageSize={state.pagination.pageSize}
              total={totalEntries ?? 0}
              onChange={handlePageChange}
              onShowSizeChange={handlePageChange}
              showSizeChanger
              showQuickJumper
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total} inspections`
              }
              pageSizeOptions={['10', '20', '50', '100']}
            />
          </PaginationContainer>
        </CardListContainer>
      </ContentContainer>
    </MainContainer>
  )
}

export const InspectionCardsList = memo(InspectionCardsListComponent)
